import React, { useEffect, useRef, useState } from 'react';
import './App.css';

import {
	HelmetProvider,
	Helmet
} from 'react-helmet-async'

import {
	useCustomNavigate,
	useCustomLocation,
	useCustomDispatch,
	useVersionCheck,
	useInactivityLogout
} from './hooks';

import { authToken } from './storage/authToken';
import { removeWorkSpaceTab } from './utils/helper/helper';
import {
	PHARMACY_HANDOVER_REQUEST_EVENT,
	PHARMACY_HANDOVER_RESPONSE_EVENT,
	getAppDateString,
} from './utils/miscellaneous/handoverEvents';

import { toast } from 'react-toastify';
import { useCustomSelectors } from './services/selectors/allSelectors';
import { subscribePharmacyRolesAndPermissions, medReviewFollowUp, subscribeMedReviewVisibility, investigateRxNotification } from './utils/miscellaneous/ably';
import useMyModules, { buildModuleGate, refreshMyModules } from './hooks/useMyModules';
import { subscribeRealtimeEvent } from './services/realtime/realtimeSocket';
import { ablyConnectionManager } from './utils/miscellaneous/ablyConnectionManager';
import { subScribeChatMessages } from './components/chat/helper';
import FrontendErrorBoundary from './components/error/FrontendErrorBoundary';
import useGlobalFrontendMonitoring from './monitoring/useGlobalFrontendMonitoring';
import { setFrontendIncidentContext } from './monitoring/reportFrontendIncident';

import { loginSuccess } from './services/actions/auth/authActions';
import { authPharmacyPermissions } from './services/actions/auth/authPermissions';

import {
	fetchPharmacyFileManagerUrl,
	fetchUserProfile,
	getPharmacyAccountabilitySettings,
	getAfterProcessPrepCheckSetting
} from './services/actions/auth/userProfileActions';

import { fetchTimesheetDates } from './services/actions/main/pharmacyTimesheetDatesActions';
import { fetchProvinceList } from './services/actions/patients/provinceListsActions';

import {
	createAction,
	actionTypes,
	CheckBookkeeperModuleAccess
} from "./services/reducers/pharmacy/other/commonReducerForInitalData";

import ActivityStatus from './assets/js/userActivityStatus';
import AdminStealthManager from './components/common/AdminStealthManager';
import AppRoutes from './routes/appRoutes';

import PortalDeliveryVideoCallManager from './components/video-call/PortalDeliveryVideoCallManager';
import PharmacistVerificationManager from './components/pharmacist/PharmacistVerificationManager';
import IncomingHandoverRequestManager from './components/pharmacy/dispense/IncomingHandoverRequestManager';
import SafeSupplyIvCodeReviewManager from './components/pharmacy/dispense/SafeSupplyIvCodeReviewManager';
import MismatchScheduleReviewManager from './components/pharmacy/dispense/MismatchScheduleReviewManager';
import UnsentScheduleSendPromptManager from './components/pharmacy/dispense/UnsentScheduleSendPromptManager';
import { canViewMismatchScheduleReview } from './components/pharmacy/dispense/mismatchScheduleReviewEvents';
import QuantityChangeReasonPopupManager from './components/pharmacy/dispense/QuantityChangeReasonPopupManager';
import DeliveredRxReasonChangeReviewPopupManager from './components/pharmacy/dispense/DeliveredRxReasonChangeReviewPopupManager';
import ReprocessedDeliveryReviewPopupManager from './components/pharmacy/dispense/ReprocessedDeliveryReviewPopupManager';
import RequiredDeliveryEmailSetupModal from './components/pharmacy/admin/RequiredDeliveryEmailSetupModal';
import DeliveryStartDateSetupModal from './components/pharmacy/admin/DeliveryStartDateSetupModal';
import RealtimeDemoManager from './components/realtime/RealtimeDemoManager';
import FlowAssignmentNotifier from './components/realtime/FlowAssignmentNotifier';
import { PatientChatNotificationBridge } from './components/patient-chat/PatientChatLauncher' ;

import { BirthdayProvider } from './context/BirthdayContext';
import BirthdayExperienceManager from './components/birthday/BirthdayExperienceManager';

import * as routesName from './routes/routeName'
import tabId from './utils/helper/tabID';

import 'react-toastify/dist/ReactToastify.css';   
import './assets/css/style.css'
import 'flowbite';

const App = () => {

	const { updateAvailable, reloadPage } = useVersionCheck()
	const navigate = useCustomNavigate()
	const token = authToken()
	const isAuthenticated = Boolean(token)
	const location = useCustomLocation()
	const dispatch = useCustomDispatch()

	const { pharmacyLoginData, pharmacyId, loginUserID, profileData, teamStatus } = useCustomSelectors()
	const [title, setTitle] = useState('OATRx - Pharmacy Management System');
	const handledPharmacyHandoverRequestsRef = useRef(new Set());
	// Health Canada recalls already announced this session, so a re-run of the
	// sync does not toast the same recall twice.
	const announcedRecallsRef = useRef(new Set());
	const myModules = useMyModules();
	const inventoryModuleAllowed = buildModuleGate(myModules).tabAllowed('INVENTORY');
	const isTeamUserRestricted = process.env.NODE_ENV === 'production' && Number(teamStatus) === 1;
	const shouldShowPharmacistReviewPopups = String(profileData?.type || '').toLowerCase() === 'pharmacist'
		&& !isTeamUserRestricted;
	const canViewMismatchSchedule = canViewMismatchScheduleReview({ profileData });

	useGlobalFrontendMonitoring({
		pharmacy_id: pharmacyId,
		pharmacy_name: pharmacyLoginData?.name || null,
		pharmacy_user_id: profileData?.user_id || null,
		module_name: 'app-shell',
		route_path: location.pathname,
	});

	useEffect(() => {
		setFrontendIncidentContext({
			pharmacy_id: pharmacyId,
			pharmacy_name: pharmacyLoginData?.name || null,
			pharmacy_user_id: profileData?.user_id || null,
			route_path: location.pathname,
			metadata: {
				app_name: 'pharmacy',
			},
		});
	}, [location.pathname, pharmacyId, pharmacyLoginData?.name, profileData?.user_id]);
	
	// Inactivity logout hook - 3 hours of inactivity triggers logout
	useInactivityLogout(isAuthenticated, 180);

	useEffect(() => {
		if (token && location.pathname !== routesName.bookkeeper_mobile_login) {
			setTitle(pharmacyLoginData?.name ? `${pharmacyLoginData.name} - OATRX` : '');
		}
	}, [token, pharmacyLoginData, location.pathname]);

	useEffect(() => {
		if (location.pathname === routesName.pharmacy_fax_log ||
			location.pathname === routesName.pharmacy_file_manager ||
			location.pathname === routesName.pharmacy_front_profile ||
			location.pathname === routesName.SETTINGS_ROUTE
		) {
			removeWorkSpaceTab()
		}
	}, [location])

	useEffect(() => {
		if (!token || !pharmacyId) return;

		const ablyConnection = subscribePharmacyRolesAndPermissions(pharmacyId, dispatch);

		return () => {
			if (ablyConnection) {
				try {
					ablyConnection.close();
				} catch (error) {
					console.error('Error closing Ably connection:', error);
				}
			}
		};
	}, [dispatch, pharmacyId, token])

	useEffect(() => {
		if (!token || !pharmacyId) return;

		const ablyConnection = subScribeChatMessages(pharmacyId, null, dispatch);

		return () => {
			if (ablyConnection) {
				try {
					ablyConnection.close();
				} catch (error) {
					console.error('Error closing Ably connection:', error);
				}
			}
		};
	}, [dispatch, pharmacyId, token]);

	useEffect(() => {
		if (!token || !pharmacyId) return;

		const ablyConnection = medReviewFollowUp(pharmacyId);

		return () => {
			if (ablyConnection) {
				try {
					ablyConnection.close();
				} catch (error) {
					console.error('Error closing Ably connection:', error);
				}
			}
		};
	}, [pharmacyId, token]);

	useEffect(() => {
		if (!token || !pharmacyId) return;

		const ablyConnection = subscribeMedReviewVisibility(pharmacyId, dispatch);

		return () => {
			if (ablyConnection) {
				try {
					ablyConnection.close();
				} catch (error) {
					console.error('Error closing Ably connection:', error);
				}
			}
		};
	}, [dispatch, pharmacyId, token]);

	useEffect(() => {
		if (!token || !pharmacyId) return;

		const ablyConnection = investigateRxNotification(pharmacyId, dispatch);

		return () => {
			if (ablyConnection) {
				try {
					ablyConnection.close();
				} catch (error) {
					console.error('Error closing Ably connection:', error);
				}
			}
		};
	}, [dispatch, pharmacyId, token]);

	// Module permissions changed somewhere in this pharmacy -> drop the cached
	// my-modules answer and refetch. Every consumer of useMyModules (top nav,
	// sidebar, profile dropdown, route guards) re-renders off the fresh gate,
	// and ModuleGuard moves anyone sitting on a page they just lost. No
	// refresh, no re-login, and it reaches every open session at once.
	useEffect(() => {
		if (!token || !pharmacyId) return;

		const subscription = subscribeRealtimeEvent('module_access_updated', ({ data }) => {
			if (Number(data?.pharmacy_id) === Number(pharmacyId)) {
				// Refresh only after an admin saves a module-permission change.
				// Normal module navigation and browser focus do not call this API.
				refreshMyModules();
			}
		});

		return () => {
			subscription.close();
		};
	}, [pharmacyId, token]);

	useEffect(() => {
		if (!token || !pharmacyId || !loginUserID) return undefined;

		const eventName = `pharmacy-${pharmacyId}`;
		const subscriptionId = `pharmacy-notifications-${eventName}-handover-events`;

		const handler = async (message) => {
			const payload = message?.data;
			if (payload?.type === 'delivery_handover_response') {
				if (
					payload?.target_type !== 'pharmacy_user'
					|| Number(payload?.target_id) !== Number(loginUserID)
					|| profileData?.type !== 'pharmacist'
					|| getAppDateString(payload?.responded_at || new Date()) !== getAppDateString()
				) {
					return;
				}

				if (payload?.request_id) {
					handledPharmacyHandoverRequestsRef.current.delete(payload.request_id);
				}

				window.dispatchEvent(new CustomEvent(PHARMACY_HANDOVER_RESPONSE_EVENT, { detail: payload }));
				return;
			}

			if (
				payload?.type !== 'delivery_handover_request'
				|| payload?.target_type !== 'pharmacy_user'
				|| Number(payload?.target_id) !== Number(loginUserID)
				|| profileData?.type !== 'pharmacist'
				|| getAppDateString(payload?.requested_at || new Date()) !== getAppDateString()
			) {
				return;
			}

			if (handledPharmacyHandoverRequestsRef.current.has(payload.request_id)) {
				return;
			}

			handledPharmacyHandoverRequestsRef.current.add(payload.request_id);
			window.dispatchEvent(new CustomEvent(PHARMACY_HANDOVER_REQUEST_EVENT, { detail: payload }));
		};

		ablyConnectionManager.subscribe('pharmacy-notifications', eventName, handler, subscriptionId);

		return () => {
			ablyConnectionManager.unsubscribe(subscriptionId);
		};
	}, [loginUserID, pharmacyId, profileData?.type, token]);

	// A Health Canada recall that matched this pharmacy's stock has already
	// quarantined the lots by the time this fires — the toast is how whoever
	// is on the floor finds out, wherever they happen to be in the portal.
	// Only recalls that hit our own shelves are announced; the rest of the
	// national feed sits quietly on the Recalls tab.
	useEffect(() => {
		if (!token || !pharmacyId || !inventoryModuleAllowed) return undefined;

		const eventName = `pharmacy-${pharmacyId}`;
		const subscriptionId = `pharmacy-notifications-${eventName}-health-canada-recalls`;

		const handler = (message) => {
			const payload = message?.data;

			if (payload?.event_type !== 'health_canada_recall_matched') return;
			if (Number(payload?.pharmacy_id) !== Number(pharmacyId)) return;

			// One toast per recall, however many tabs are open or times the
			// sync re-runs while somebody is signed in.
			const seen = announcedRecallsRef.current;
			if (seen.has(payload.external_nid)) return;
			seen.add(payload.external_nid);

			toast.warn(payload.toast || 'A Health Canada recall matches stock you hold.', {
				autoClose: false,
				onClick: () => navigate(routesName.pharmacy_inventory_accountability),
			});
		};

		ablyConnectionManager.subscribe('pharmacy-notifications', eventName, handler, subscriptionId);

		return () => {
			ablyConnectionManager.unsubscribe(subscriptionId);
		};
	}, [inventoryModuleAllowed, navigate, pharmacyId, token]);

	useEffect(() => {
		if (typeof BroadcastChannel === 'undefined') {
			return;
		}

		const broadcastChannel = new BroadcastChannel("logout-channel");
		const authChannel = new BroadcastChannel('auth-channel');

		broadcastChannel.onmessage = (event) => {
			if (event.data === "logout") {
				navigate("/");
				window.location.reload();
			}
		};

		authChannel.onmessage = (event) => {
			if (event.data.type === 'login') {
				const isCurrentTab = event.data.tabId === tabId;
				const response = event?.data?.response
				const loginPharmacyData = event?.data?.loginPharmacyData
				const settingsData = event?.data?.settingsData
				if (!isCurrentTab) {
					dispatch(loginSuccess(response));
					dispatch(authPharmacyPermissions())
					dispatch(fetchUserProfile())
					dispatch(fetchPharmacyFileManagerUrl())
					dispatch(fetchTimesheetDates())
					dispatch(fetchProvinceList())
					dispatch(getPharmacyAccountabilitySettings(loginPharmacyData?.finalchk_at))
					dispatch(getAfterProcessPrepCheckSetting(settingsData?.pre_after_process ?? false))
					dispatch(createAction(actionTypes.UPDATE_LABEL_TO_MARGIN.SUCCESS, {
						data: loginPharmacyData?.label_top_margin,
						requestType: 'updateLabelToMargin'
					}))
					dispatch(createAction(actionTypes.UPDATE_LABEL_PRINT_HEADER.SUCCESS, {
						data: !!loginPharmacyData?.label_print_header,
						requestType: 'updateLabelPrintHeader'
					}))
					dispatch(createAction(actionTypes.UPDATE_LABEL_HEADER_TOP_MARGIN.SUCCESS, {
						data: loginPharmacyData?.label_header_top_margin ?? 0,
						requestType: 'updateLabelHeaderTopMargin'
					}))
					dispatch(createAction(actionTypes.UPDATE_LABEL_PRINT_TYPE.SUCCESS, {
						data: loginPharmacyData?.label_print_type || 'regular',
						requestType: 'updateLabelPrintType'
					}))
					dispatch(createAction(actionTypes.UPDATE_LABEL_PRINT_LOGO.SUCCESS, {
						data: !!loginPharmacyData?.label_print_logo,
						requestType: 'updateLabelPrintLogo'
					}))
					dispatch(createAction(actionTypes.UPDATE_LABEL_LEFT_MARGIN.SUCCESS, {
						data: loginPharmacyData?.label_left_margin ?? 0,
						requestType: 'updateLabelLeftMargin'
					}))
					dispatch(createAction(actionTypes.UPDATE_LABEL_SIG_GAP.SUCCESS, {
						data: loginPharmacyData?.label_sig_gap ?? 0,
						requestType: 'updateLabelSigGap'
					}))
					// Check bookkeeper module access on login success
					dispatch(CheckBookkeeperModuleAccess({ fetchType: 'checkBookkeeperModuleAccess' }))
					navigate("/dashboard");
				}
			}
		};
		// Cleanup listener on component unmount
		return () => {
			broadcastChannel.close();
			authChannel.close();
		};
	}, [dispatch, navigate]);

	return (
		<>
			{updateAvailable && (
				<div className="fixed bottom-0 left-0 right-0 bg-yellow-300 text-black p-3 text-center z-50">
					New version available. Please refresh.
					<button className="ml-2 underline" onClick={reloadPage}>Reload</button>
				</div>
			)}

			<FrontendErrorBoundary
				resetKey={location.pathname}
				context={{
					pharmacy_id: pharmacyId,
					pharmacy_name: pharmacyLoginData?.name || null,
					pharmacy_user_id: profileData?.user_id || null,
					module_name: 'route-shell',
					route_path: location.pathname,
				}}
			/>
				
			{isAuthenticated && (
				<>
					<PortalDeliveryVideoCallManager />
					<IncomingHandoverRequestManager />
					<SafeSupplyIvCodeReviewManager
						pharmacyId={pharmacyId}
						isPharmacist={profileData?.type === 'pharmacist'}
					/>
					<MismatchScheduleReviewManager
						pharmacyId={pharmacyId}
						isPharmacist={canViewMismatchSchedule}
					/>

					{shouldShowPharmacistReviewPopups && (
						<>
							<UnsentScheduleSendPromptManager
								pharmacyId={pharmacyId}
								isPharmacist
								userId={profileData?.user_id}
							/>
							<QuantityChangeReasonPopupManager />
							<DeliveredRxReasonChangeReviewPopupManager />
							<ReprocessedDeliveryReviewPopupManager />
						</>
					)}

					<RequiredDeliveryEmailSetupModal />
					<DeliveryStartDateSetupModal />
					<RealtimeDemoManager />
					<FlowAssignmentNotifier />
					<PatientChatNotificationBridge />
				</>
			)}
			
			<BirthdayProvider>
				{/* Admin screen privacy (F9 x5). Renders nothing; inert for
				    non-admins and while logged out. */}
				<AdminStealthManager />
				{isAuthenticated && <ActivityStatus />}
				{isAuthenticated && <PharmacistVerificationManager />}
				{isAuthenticated && <BirthdayExperienceManager />}
				<AppRoutes />
			</BirthdayProvider>

			<HelmetProvider>
				<Helmet>
					<link href="https://cdn.jsdelivr.net/npm/flatpickr/dist/flatpickr.min.css" rel="stylesheet"></link>
					<script src="https://cdn.jsdelivr.net/npm/flatpickr"></script>
					{/* <script src="https://unpkg.com/@material-tailwind/html@latest/scripts/ripple.js"></script> */}
					<title>{title}</title>
				</Helmet>
			</HelmetProvider>
		</>
	)
}

export default App
